/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import { createUseStyles } from 'react-jss';
import {
  Dropdown,
  IDropdown,
  IRefObject,
  ISlider,
  ITextField,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  Slider,
  Stack,
  StackItem,
  Text,
  TextField,
  Toggle,
  Dialog,
  DialogType,
} from '@fluentui/react';
import { useLLMSetting } from '../Contexts';
import './Panel.css';
import {
  LLMModel, PromptingMethod, defaultChatPrompt, defaultEvalPrompt, defaultPromotePrompt, getDeploymentName,
} from '../Contexts/LLMSettingContext';
import { PromptTest } from '../../Test/PromptTest';
import { useTestSetting } from '../../Test/TestSettingContext';
import { userModeSetting } from './userModeSetting';
import { PlaygroundTheme } from '../../Theme';

export type DeveloperPanelProps = {
  isOpen: boolean,
  onClose: () => void,
};

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  panelContentContainer: {
    height: '100%',
    paddingTop: 20,

    '& .ms-TextField-fieldGroup::after,.ms-Dropdown::after': {
      borderColor: theme.themePrimary,
    },
  },
  SaveButton: {
    backgroundColor: theme.themePrimary,
    borderColor: theme.themePrimary,
    '&:hover': {
      backgroundColor: theme.themeDarkAlt,
      borderColor: theme.themeDarkAlt,
    },
  },
  TestButton: {
    backgroundColor: theme.themePrimary,
    borderColor: theme.themePrimary,
    '&:hover': {
      backgroundColor: theme.themeDarkAlt,
      borderColor: theme.themeDarkAlt,
    },
  },
}));

export const DeveloperPanel: React.FunctionComponent<DeveloperPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const classes = useStyles();
  const {
    temperature,
    maxTokens,
    chatPrompt,
    promotePrompt,
    evalPrompt,
    prompt,
    prompts,
    model,
    conversationId,
    promptingMethod,
    update,
    ignoreDefaultFewShotExamples,
    deployment,
  } = useLLMSetting();
  const {
    mockUserMaxTokens,
    mockUserTemperature,
    mockUserPrompt,
    evaluatorMaxTokens,
    evaluatorTemperature,
    evaluatorPrompt,
  } = useTestSetting();
  const temperatureSliderRef = React.useRef<ISlider>();
  const maxTokensSliderRef = React.useRef<ISlider>();
  const chatPromptTextFieldRef = React.useRef<ITextField>();
  const promotePromptTextFieldRef = React.useRef<ITextField>();
  const evalPromptTextFieldRef = React.useRef<ITextField>();
  const promptTextFieldRef = React.useRef<ITextField>();
  const promptText1FieldRef = React.useRef<ITextField>();
  const promptText2FieldRef = React.useRef<ITextField>();
  const promptText3FieldRef = React.useRef<ITextField>();
  const promptText4FieldRef = React.useRef<ITextField>();
  const promptText5FieldRef = React.useRef<ITextField>();

  const modelDropdownRef = React.useRef<IDropdown>();
  const promptingMethodDropdownRef = React.useRef<IDropdown>();

  const mockUserTemperatureSliderRef = React.useRef<ISlider>();
  const mockUserMaxTokensSliderRef = React.useRef<ISlider>();
  const mockUserPromptTextFieldRef = React.useRef<ITextField>();

  const evaluatorTemperatureSliderRef = React.useRef<ISlider>();
  const evaluatorMaxTokensSliderRef = React.useRef<ISlider>();
  const evaluatorPromptTextFieldRef = React.useRef<ITextField>();

  const deploymentTextFieldRef = React.useRef<ITextField>();

  const [useDefaultFewShowExample, setUseDefaultFewShowExample] = React.useState<boolean>(!ignoreDefaultFewShotExamples);
  const [showTestDialog, setShowTestDialog] = React.useState<boolean>(false);
  const [deploymentValue, setDeploymentValue] = React.useState(model === LLMModel.Companion ? deployment : 'gpt-35-turbo'); // use preset Companion deployment or default gpt-35-turbo

  const dialogContentProps = {
    type: DialogType.normal,
    showCloseButton: true,
    closeButtonAriaLabel: 'Close',
  };

  const modalProps = {
    isBlocking: true,
  };

  const getCurrentPrompts = () => [
    promptText1FieldRef.current?.value,
    promptText2FieldRef.current?.value,
    promptText3FieldRef.current?.value,
    promptText4FieldRef.current?.value,
    promptText5FieldRef.current?.value,
  ] as string[];

  const onSave = React.useCallback((testing = false) => {
    const currentSelectedPromptingMethod = (promptingMethodDropdownRef.current?.selectedOptions[0].key ?? PromptingMethod.Default) as PromptingMethod;

    if (currentSelectedPromptingMethod === PromptingMethod.UserMode) {
      update({
        temperature: userModeSetting.temperature,
        maxTokens: userModeSetting.maxTokens,
        chatPrompt: '',
        promotePrompt: '',
        evalPrompt: '',
        // eslint-disable-next-line max-len
        prompt: modelDropdownRef.current?.selectedOptions[0].key === LLMModel.Companion && deploymentTextFieldRef.current?.value === 'EE_1' ? userModeSetting.newPrompt : userModeSetting.prompt,
        prompts: [],
        model: modelDropdownRef.current?.selectedOptions[0].key as LLMModel,
        deployment: modelDropdownRef.current?.selectedOptions[0].key === LLMModel.Companion ? deploymentTextFieldRef.current?.value : getDeploymentName(modelDropdownRef.current?.selectedOptions[0].key as LLMModel),
        promptingMethod: currentSelectedPromptingMethod,
        ignoreDefaultFewShotExamples: true,
        conversationId,
      });

      if (!testing) {
        onClose();
      }
      return;
    }

    const isInputValidForGpt = Number.isFinite(temperatureSliderRef.current?.value ?? NaN)
            && Number.isFinite(maxTokensSliderRef.current?.value ?? NaN);
    const isGpt35BasicModel = currentSelectedPromptingMethod === PromptingMethod.Default;
    const isInputValidForGpt35Basic = typeof promptTextFieldRef.current?.value === 'string';
    const isInputValidForGpt35SeparatePrompts = typeof chatPromptTextFieldRef.current?.value === 'string'
            && typeof promotePromptTextFieldRef.current?.value === 'string'
            && typeof evalPromptTextFieldRef.current?.value === 'string';
    const isInputValidForGpt35MultiSystemPrompt = getCurrentPrompts().every((p) => typeof p === 'string');

    const isInputValid = isInputValidForGpt
            && ((!isGpt35BasicModel || isInputValidForGpt35Basic)
                && ((!(currentSelectedPromptingMethod === PromptingMethod.MultipleSystemPrompt) || isInputValidForGpt35MultiSystemPrompt))
                && ((!(currentSelectedPromptingMethod === PromptingMethod.SeparatePrompts) || isInputValidForGpt35SeparatePrompts)));

    if (isInputValid) {
      update({
        temperature: temperatureSliderRef.current?.value as number,
        maxTokens: maxTokensSliderRef.current?.value as number,
        chatPrompt: chatPromptTextFieldRef.current?.value || '',
        promotePrompt: promotePromptTextFieldRef.current?.value || '',
        evalPrompt: evalPromptTextFieldRef.current?.value || '',
        prompt: promptTextFieldRef.current?.value || '',
        prompts: getCurrentPrompts(),
        model: modelDropdownRef.current?.selectedOptions[0].key as LLMModel,
        deployment: modelDropdownRef.current?.selectedOptions[0].key === LLMModel.Companion ? deploymentValue : getDeploymentName(modelDropdownRef.current?.selectedOptions[0].key as LLMModel),
        promptingMethod: currentSelectedPromptingMethod,
        ignoreDefaultFewShotExamples: !useDefaultFewShowExample,
        conversationId,
        stopSequences: ['|endoftext|'],
      });

      if (!testing) {
        onClose();
      }
    }
  }, [onClose, update, conversationId, useDefaultFewShowExample, deploymentValue]);

  return (
    <Panel
      isOpen={isOpen}
      headerText="Developer Panel"
      onRenderFooterContent={() => (
        <Stack horizontal>
          <PrimaryButton className={classes.SaveButton} onClick={() => onSave(false)}>Save</PrimaryButton>
          <PrimaryButton
            className={classes.TestButton}
            onClick={() => {
              onSave(true);
              setShowTestDialog(true);
            }}
            style={{ marginLeft: '20px' }}
          >
            Test
          </PrimaryButton>
        </Stack>
      )}
      type={PanelType.custom}
      customWidth="450px"
      isFooterAtBottom
      isBlocking
      isLightDismiss={false}
      hasCloseButton={false}
    >
      <Stack
        className={classes.panelContentContainer}
        tokens={{
          childrenGap: 12,
        }}
      >
        <MessageBar
          messageBarType={MessageBarType.warning}
          isMultiline={false}
        >
          Values put here WILL BE LOST. Please keep a copy yourself.
        </MessageBar>
        <Dropdown
          label="Model"
          defaultSelectedKey={model}
          componentRef={modelDropdownRef as IRefObject<IDropdown>}
          options={[
            { key: LLMModel.Davinci003, text: 'Text-Davinci-003', disabled: true },
            { key: LLMModel.Gpt35Turbo, text: 'GPT-3.5' },
            { key: LLMModel.Gpt4, text: 'GPT-4' },
            { key: LLMModel.Companion, text: 'Companion chatbot' },
          ]}
          multiSelect={false}
          onChange={(_, option) => {
            if (option) {
              update({
                model: option.key as LLMModel,
              });
            }
          }}
        />
        {model === LLMModel.Companion && (
        <TextField
          label="Deployment"
          componentRef={deploymentTextFieldRef as IRefObject<ITextField>}
          defaultValue="FT_3_7"
          placeholder="Azure deployment name"
          value={deploymentValue}
          onChange={(_, newValue) => {
            setDeploymentValue(newValue || '');
          }}
        />
        )}
        <Dropdown
          label="Prompting Method"
          defaultSelectedKey={promptingMethod}
          componentRef={promptingMethodDropdownRef as IRefObject<IDropdown>}
          options={[
            { key: PromptingMethod.Default, text: 'Default' },
            ...(model === LLMModel.Companion ? [] : [
              { key: PromptingMethod.MultipleSystemPrompt, text: 'Multiple System Prompt' },
              { key: PromptingMethod.SeparatePrompts, text: 'Separate Prompts' },
            ]),
            { key: PromptingMethod.UserMode, text: 'User mode' },
          ]}
          multiSelect={false}
          onChange={(_, option) => {
            if (option) {
              update({
                prompt: promptTextFieldRef.current?.value || '',
                prompts: getCurrentPrompts(),
                chatPrompt: option.key === PromptingMethod.SeparatePrompts ? defaultChatPrompt : '',
                evalPrompt: option.key === PromptingMethod.SeparatePrompts ? defaultEvalPrompt : '',
                promotePrompt: option.key === PromptingMethod.SeparatePrompts ? defaultPromotePrompt : '',
                ignoreDefaultFewShotExamples: !useDefaultFewShowExample,
                promptingMethod: option.key as PromptingMethod,
              });
            }
          }}
        />
        {(promptingMethod !== PromptingMethod.SeparatePrompts && promptingMethod !== PromptingMethod.UserMode) && (
        <Toggle
          label="Use default few-shot examples"
          defaultChecked={useDefaultFewShowExample}
          onText="On"
          offText="Off"
          onChange={(_, checked) => {
            if (checked !== undefined) {
              setUseDefaultFewShowExample(checked);
              update({
                temperature: temperatureSliderRef.current?.value as number,
                maxTokens: maxTokensSliderRef.current?.value as number,
                prompt: promptTextFieldRef.current?.value || '',
                prompts: getCurrentPrompts(),
                chatPrompt: '',
                evalPrompt: '',
                promotePrompt: '',
                model: modelDropdownRef.current?.selectedOptions[0].key as LLMModel,
                ignoreDefaultFewShotExamples: !checked,
                conversationId,
              });
            }
          }}
        />
        )}
        {promptingMethod !== PromptingMethod.UserMode && (
        <Slider
          label="Temperature"
          componentRef={temperatureSliderRef as IRefObject<ISlider>}
          defaultValue={temperature}
          min={0}
          max={1}
          step={0.1}
        />
        )}
        {promptingMethod !== PromptingMethod.UserMode && (
        <StackItem>
          <Slider
            label="Max Tokens for Generated Text"
            componentRef={maxTokensSliderRef as IRefObject<ISlider>}
            defaultValue={maxTokens}
            min={16}
            max={1024}
            step={4}
          />
          <Text variant="small">
            The longer the prompt below and your chat history are, the smaller this value has to be. Or you will see errors.
          </Text>
        </StackItem>
        )}
        {
                promptingMethod === PromptingMethod.SeparatePrompts ? (
                  <>
                    <TextField
                      label="Chat Prompt"
                      componentRef={chatPromptTextFieldRef as IRefObject<ITextField>}
                      defaultValue={chatPrompt}
                      placeholder="You will need some prompt to start with."
                      multiline
                      autoAdjustHeight
                    />
                    <TextField
                      label="Promote Prompt"
                      componentRef={promotePromptTextFieldRef as IRefObject<ITextField>}
                      defaultValue={promotePrompt}
                      multiline
                      autoAdjustHeight
                      placeholder={'Be sure to ask for a response in the format of {"answer":"","query":""}'}
                    />
                    <TextField
                      label="Evaluation Prompt"
                      componentRef={evalPromptTextFieldRef as IRefObject<ITextField>}
                      defaultValue={evalPrompt}
                      multiline
                      autoAdjustHeight
                      placeholder={'Be sure to include "%s" as a placeholder for conversation history, and ask for a response in the format of {"score":"","explanation":""}'}
                    />
                  </>
                ) : null
            }
        {promptingMethod === PromptingMethod.Default && (
        <TextField
          label="Prompt"
          componentRef={promptTextFieldRef as IRefObject<ITextField>}
          defaultValue={prompt}
          placeholder="You will need some prompt to start with."
          multiline
          autoAdjustHeight
        />
        )}
        {promptingMethod === PromptingMethod.MultipleSystemPrompt
                && (
                <>
                  <TextField
                    label="Prompt 1"
                    componentRef={promptText1FieldRef as IRefObject<ITextField>}
                    defaultValue={prompts[0]}
                    placeholder="You will need some prompt to start with."
                    multiline
                    autoAdjustHeight
                  />
                  <TextField
                    label="Prompt 2"
                    componentRef={promptText2FieldRef as IRefObject<ITextField>}
                    defaultValue={prompts[1]}
                    placeholder="You will need some prompt to start with."
                    multiline
                    autoAdjustHeight
                  />
                  <TextField
                    label="Prompt 3"
                    componentRef={promptText3FieldRef as IRefObject<ITextField>}
                    defaultValue={prompts[2]}
                    placeholder="You will need some prompt to start with."
                    multiline
                    autoAdjustHeight
                  />
                  <TextField
                    label="Prompt 4"
                    componentRef={promptText4FieldRef as IRefObject<ITextField>}
                    defaultValue={prompts[3]}
                    placeholder="You will need some prompt to start with."
                    multiline
                    autoAdjustHeight
                  />
                  <TextField
                    label="Prompt 5"
                    componentRef={promptText5FieldRef as IRefObject<ITextField>}
                    defaultValue={prompts[4]}
                    placeholder="You will need some prompt to start with."
                    multiline
                    autoAdjustHeight
                  />
                </>
                )}
      </Stack>
      <Dialog
        hidden={!showTestDialog}
        onDismiss={() => {
          setShowTestDialog(false);
        }}
        minWidth={800}
        dialogContentProps={{
          ...dialogContentProps,
          title: (
            <div>
              <Slider
                label="Mock User Temperature"
                componentRef={mockUserTemperatureSliderRef as IRefObject<ISlider>}
                defaultValue={mockUserTemperature}
                min={0}
                max={1}
                step={0.1}
              />
              <Slider
                label="Mock User Max Tokens"
                componentRef={mockUserMaxTokensSliderRef as IRefObject<ISlider>}
                defaultValue={mockUserMaxTokens}
                min={16}
                max={1024}
                step={4}
              />
              <TextField
                label="Mock User Prompt"
                componentRef={mockUserPromptTextFieldRef as IRefObject<ITextField>}
                defaultValue={mockUserPrompt}
                placeholder="You will need mock user prompt to start with."
                multiline
                style={{ height: '150px' }}
              />
              <Slider
                label="Evaluator Temperature"
                componentRef={evaluatorTemperatureSliderRef as IRefObject<ISlider>}
                defaultValue={evaluatorTemperature}
                min={0}
                max={1}
                step={0.1}
                style={{ marginTop: '20px' }}
              />
              <Slider
                label="Evaluator Max Tokens"
                componentRef={evaluatorMaxTokensSliderRef as IRefObject<ISlider>}
                defaultValue={evaluatorMaxTokens}
                min={16}
                max={1024}
                step={4}
              />
              <TextField
                label="Evaluator Prompt"
                componentRef={evaluatorPromptTextFieldRef as IRefObject<ITextField>}
                defaultValue={evaluatorPrompt}
                placeholder="You will need evaluator prompt to start with."
                multiline
                style={{ height: '150px' }}
              />
              <PromptTest />
            </div>
          ),
        }}
        modalProps={modalProps}
      >
        {/* <DialogFooter>
                {!isTesting && <PrimaryButton
                  autoFocus
                  className="dialog-confirm"
                  onClick={onTest}
                  text={'Apply'}
                />}
                {!isTesting && <DefaultButton
                  className="dialog-cancel"
                  onClick={isTesting ? () => {
                    setIsTesting(false)} : () => {
                    setShowTestDialog(false)
                    setIsTesting(false)
                  }}
                  text={'Cancel'}
                />}
            </DialogFooter> */}
      </Dialog>
    </Panel>
  );
};
